import type { MedicationDose } from '../lib/to-medication-order';
import { MED_FREQUENCY } from '../lib/medication-frequency';

/**
 * Does the structured schedule contradict what the clinician actually said?
 *
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 *  READ THIS FIRST — THE ROOT CAUSE IS UPSTREAM, AND IT IS NOT A PARSER BUG
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 *
 * **GPT-4 REWRITES THE DICTATED PHRASE BEFORE THE CLIENT EVER SEES THE PAYLOAD.** "twice weekly"
 * has already become "BID" by the time anything on this side runs, so `parseFrequency` is handed an
 * ALREADY-CORRUPTED STRING — and parses it perfectly. Measured, not assumed:
 *
 *     parseFrequency("twice weekly")  ->  undefined     // our parser falls through and refuses to
 *                                                       // guess: the count-word rule needs a day
 *                                                       // token, and it will not invent one
 *     parseFrequency("bid")           ->  twice daily   // correct — on a string that is a lie
 *
 * **DO NOT TRY TO FIX THIS BY IMPROVING THE PARSER.** No parser change can help, because the parser
 * never sees the clinician's words. Every component on this side behaves correctly on the input it
 * is given; the input was wrong before it arrived. Anyone who reaches for `parse-medication-sig.ts`
 * or `medication-frequency.ts` to "catch twice weekly properly" is walking into a dead end, and it
 * is an expensive one — the code there is right, so the work looks like it should be paying off
 * right up until it doesn't.
 *
 * The loss happens server-side, in SOAP generation, one hop before the wire. A real fix is a
 * prompt/pipeline change — carry the VERBATIM frequency phrase alongside the normalised code — and
 * lives in the API, not in this directory. Until that exists, the only witness on this side is the
 * TRANSCRIPT, which still holds what was actually said. That is the entire reason this guard reads
 * an utterance rather than a payload.
 *
 * ── THE DEFECT, PROD-2018 (High) ──────────────────────────────────────────────────────────────
 *
 * Dictate *"250 units twice weekly"* and the prescription comes back **BID — fourteen doses a week
 * instead of two**. Name right, dose right, schedule silently rewritten, and nothing on screen
 * looks wrong. It is the only open ambient defect that can injure a patient.
 *
 * ── THIS FILE DETECTS. IT DOES NOT GUARANTEE ANYTHING ─────────────────────────────────────────
 *
 * It used to also export `withoutContradictedSchedule`, a silent strip that removed the contested
 * schedule on the way to the order seed. That guaranteed the wrong value was not written, and
 * guaranteed nobody was told — which is how a detector this file has had since day one produced a
 * defect nobody could see.
 *
 * The guarantee now lives in the ACCEPT DECISION. `decideAccept` (`accept-rules.ts`) refuses to
 * return a writing decision while a contest is unresolved, so a schedule contested here cannot
 * reach a prescription until a clinician has answered it. Detection here, refusal there, and
 * nothing in between that quietly makes a bad value safe.
 *
 * ── THE FALSE POSITIVE THIS IS BUILT AROUND ───────────────────────────────────────────────────
 *
 * *"Take two tablets twice daily for two weeks"* contains "weeks" and is **not** a weekly schedule
 * — it is a daily schedule with a two-week DURATION. A naive `/week/` test flags it, withholds a
 * correct schedule, and trains the clinician to ignore the warning. A guard that cries wolf on
 * ordinary prescribing is worse than no guard, because it is dismissed exactly when it is right.
 *
 * So periodicity is read only in FREQUENCY position, never in duration position: a week token
 * governed by `for` / `over` / `during`, or preceded by a number-and-unit duration phrase, is a
 * duration and is ignored.
 *
 * ── WHAT IT REFUSES TO DO ─────────────────────────────────────────────────────────────────────
 *
 *   1. **Never contradicts on silence.** No utterance, or no periodicity stated, means no finding.
 *      Same rule as the diagnosis reconciler: absence of evidence is not disagreement.
 *   2. **Never rewrites the schedule.** It reports a contradiction. It does not "fix" BID to
 *      twice-weekly — inferring the right schedule from prose is the same class of guess that
 *      caused the defect. The clinician reads the verbatim sig and types it.
 *   3. **Never fires on a schedule the system cannot express.** `days_interval` and
 *      `specific_days` exist, so a weekly regimen HAS a representation. This guard says the daily
 *      one is wrong; choosing the right one is the clinician's.
 */

/** The periodicity a clinician stated, when they stated one in frequency position. */
export type StatedPeriodicity = 'daily' | 'weekly' | 'monthly' | 'interval';

/**
 * The one frequency code that means "n times per DAY".
 *
 * Bound to `MED_FREQUENCY.everyDay` rather than written as a literal, deliberately. The wire value
 * is `everyday`; the plausible-looking `every_day` is the PROD-1445 spelling that mis-parses in the
 * e-prescription path, and `MedicationDose.frequency`'s own doc comment still lists it. A literal
 * here would either miss the real value or silently accept the broken one — and this guard exists
 * because a value that looks right and is wrong reached a prescription.
 */
const PER_DAY_CODE: string = MED_FREQUENCY.everyDay;

/**
 * Duration position: a week/month token here is how long the course runs, not how often a dose is
 * taken. `for two weeks`, `over 3 months`, `during the next week`, and the bare `x N weeks` form.
 */
const DURATION_CONTEXT =
  /\b(?:for|over|during|across|in|within|after)\s+(?:the\s+)?(?:next\s+)?[\w-]*\s*(weeks?|months?)\b/gi;

/**
 * A COUNT IN FRONT MAKES IT A FREQUENCY, AND IT IS READ BEFORE ANY STRIPPING.
 *
 * Andrew found that `come back in a week` reads as a weekly DOSE — `in` was missing from the
 * duration list above, so a follow-up interval was withholding a correct twice-daily schedule. His
 * fix was to add `in|within|after`, and he reported it broke no true positives.
 *
 * It breaks two, and this is why they are handled here instead. Adding `in` to the strip list also
 * eats the `in a week` out of **"twice in a week"** and **"three times in a week"**, leaving a bare
 * "twice " that matches nothing — a stated twice-weekly regimen silently stops being detected,
 * which is the direction that ends in fourteen doses where two were said.
 *
 * The disambiguator is the count: `twice in a week` is a frequency, `come back in a week` is a
 * duration, and the only difference is the word in front. So a count-led form is matched FIRST,
 * on the raw utterance, and wins before the duration strip can see it.
 *
 * NUMBER WORDS are included, and their absence was a pre-existing gap rather than one this change
 * introduced: `WEEKLY` only ever had `\d+\s*times?`, so `three times a week` — an entirely
 * ordinary way to say it aloud — matched nothing, while `3 times per week` matched. A dictation
 * pipeline sees the spoken form far more often than the digit.
 */
const COUNT_FREQUENCY =
  /\b(?:once|twice|thrice|(?:\d+|one|two|three|four|five|six|seven|eight|nine|ten)\s*times?)\s+(?:a|per|each|in\s+a|in\s+the)?\s*(weeks?|months?)\b/i;

/**
 * `every N weeks|months` — the interval regimens, which matched NOTHING before.
 *
 * Andrew, and this is the half that mattered: *"`every N weeks|months` matches nothing — every two
 * weeks, every four weeks, every six months all fall through. Six of eight real interval regimens.
 * That fails TOWARD harm: render 'every four weeks' as a daily code and the contradiction never
 * fires."* Verified by running the regexes; all three returned undefined.
 *
 * `every other week` is folded in here rather than left in WEEKLY. It means the same thing as
 * `every two weeks`, and returning `weekly` for one and `interval` for the other would be a
 * distinction no reader could defend. The guard's action is identical either way; only the word in
 * the message changes, and the message quotes the matched phrase.
 */
const INTERVAL =
  /\bevery\s+(?:other|\d+|one|two|three|four|five|six|seven|eight|nine|ten|twelve)\s+(weeks?|months?)\b/i;

/** Frequency position: how often, not how long. */
const WEEKLY = /\b(?:(?:once|twice|thrice|\d+\s*times?)\s+(?:a|per|each)?\s*week(?:ly)?|weekly|every\s+week|per\s+week|a\s+week|on\s+alternate\s+weeks|every\s+other\s+week)\b/i;
const MONTHLY = /\b(?:(?:once|twice|thrice|\d+\s*times?)\s+(?:a|per|each)?\s*month(?:ly)?|monthly|every\s+month|per\s+month|a\s+month)\b/i;
const DAILY = /\b(?:daily|every\s+day|each\s+day|a\s+day|per\s+day|nocte|mane|od|bd|bid|tds|tid|qds|qid)\b/i;

/**
 * Strip duration phrases before reading periodicity, so "for two weeks" cannot be read as a weekly
 * schedule. Replaced with a space rather than deleted, so adjacent words do not fuse into a token
 * that matches something else.
 */
function withoutDurations(text: string): string {
  return text.replace(DURATION_CONTEXT, ' ');
}

/**
 * The periodicity stated in frequency position, or undefined when none was.
 *
 * ── A LIMITATION, STATED RATHER THAN HIDDEN ───────────────────────────────────────────────────
 *
 * The utterance is a WHOLE TURN, and a turn can cover more than one medication:
 *
 *     "take one tablet daily, and the injection once a week"
 *
 * Two periodicities, and nothing here knows which medication either attaches to. Weekly is checked
 * first, so such a turn reads as weekly — which fails SAFE for this guard (it flags rather than
 * stays silent, and a flag is reviewed by a clinician) but is a guess, not a reading.
 *
 * That ordering was originally justified in this comment with a rationale no test supported —
 * inverting weekly and daily left all 28 tests green. The rationale is now the honest one: the
 * order decides multi-medication turns and nothing else, and the direction was chosen because
 * over-flagging is the survivable error. Narrowing the utterance to the medication's own span is
 * the real fix and needs the entity offsets, which is separate work.
 */
export function statedPeriodicity(utterance: string | undefined): StatedPeriodicity | undefined {
  return readPeriodicity(utterance)?.kind;
}

/** The periodicity AND the words that said it, so a message can quote rather than categorise. */
export interface PeriodicityHit {
  kind: StatedPeriodicity;
  /** Verbatim from the utterance — "every four weeks", not "interval". */
  phrase: string;
}

export function readPeriodicity(utterance: string | undefined): PeriodicityHit | undefined {
  if (!utterance?.trim()) return undefined;
  const raw = utterance.toLowerCase();

  // BEFORE the strip, both of them: a count-led form and an explicit interval are unambiguous
  // frequencies, and the duration pattern would eat parts of either.
  const counted = COUNT_FREQUENCY.exec(raw);
  if (counted) {
    return { kind: counted[1].startsWith('month') ? 'monthly' : 'weekly', phrase: counted[0].trim() };
  }
  const interval = INTERVAL.exec(raw);
  if (interval) return { kind: 'interval', phrase: interval[0].trim() };

  const text = withoutDurations(raw);
  const weekly = WEEKLY.exec(text);
  if (weekly) return { kind: 'weekly', phrase: weekly[0].trim() };
  const monthly = MONTHLY.exec(text);
  if (monthly) return { kind: 'monthly', phrase: monthly[0].trim() };
  const daily = DAILY.exec(text);
  if (daily) return { kind: 'daily', phrase: daily[0].trim() };
  return undefined;
}

export interface FrequencyContradiction {
  /** What the clinician said. */
  stated: StatedPeriodicity;
  /** The words that said it, verbatim — quoted in the message instead of a category label. */
  phrase: string;
  /** What the structured schedule would give them, in doses per day. */
  structuredPerDay: number;
  /** For the message a clinician reads — never assembled by a model. */
  utterance: string;
}

/**
 * A contradiction exists when the clinician stated a weekly or monthly regimen and the structured
 * dose carries a per-day schedule.
 *
 * The reverse is deliberately NOT a contradiction: a stated daily regimen against a `days_interval`
 * schedule is under-specified rather than wrong, and flagging it would fire on ordinary prescribing.
 */
export function frequencyContradiction(
  dose: MedicationDose | undefined,
  utterance: string | undefined,
): FrequencyContradiction | undefined {
  if (!dose || !utterance) return undefined;                        // rule (1)

  const code = (dose.frequency ?? '').toLowerCase();
  if (code !== PER_DAY_CODE) return undefined;

  const hit = readPeriodicity(utterance);
  // `interval` joins weekly and monthly: "every four weeks" against a per-day code is a 28-fold
  // error, and it was the most common of the interval forms to fall through undetected.
  if (!hit || hit.kind === 'daily') return undefined;
  const stated = hit.kind;

  // `timesADay` absent on an every_day schedule still means at least once a day, which already
  // contradicts a weekly regimen — seven doses where two were said.
  const perDay = dose.timesADay ?? 1;

  return { stated, phrase: hit.phrase, structuredPerDay: perDay, utterance: utterance.trim() };
}
